import { useContext, useEffect , useState} from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import { motion } from "framer-motion";
import LivreurInfoModal from "./LivreurInfoModal";


// simple animation
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function MarketplaceLivreur() {
  const { t, isRTL } = useContext(LanguageContext);
const [openModal, setOpenModal] = useState(false);

  const benefits = t("MarketplaceLivreur.benefits", { returnObjects: true });
  const steps = t("MarketplaceLivreur.steps", { returnObjects: true });
  const whyJoinList = t("MarketplaceLivreur.whyJoinList", { returnObjects: true });
  const cities = t("MarketplaceLivreur.cities", { returnObjects: true });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <section
      dir={isRTL ? "rtl" : "ltr"}
      className="relative bg-white overflow-hidden"
    >

      {/* HERO */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 py-28 text-white">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mx-auto max-w-5xl px-6 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold">
            {t("MarketplaceLivreur.heroTitle")}
          </h1>

<motion.p
  initial={{ scale: 0.95, opacity: 0 }}
  animate={{ scale: [0.95, 1.05, 1], opacity: 1 }}
  transition={{
    duration: 1.5,
    repeat: Infinity,
    repeatType: "reverse",
    ease: "easeInOut",
  }}
  className="mt-6 inline-block rounded-full bg-white/10 px-6 py-3 text-2xl md:text-3xl font-bold text-white backdrop-blur shadow-[0_0_20px_rgba(255,255,255,0.3)]"
>
  {t("MarketplaceLivreur.heroSubtitle")}
</motion.p>



          <p className="mt-4 max-w-3xl mx-auto opacity-90">
            {t("MarketplaceLivreur.heroDescription")}
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <button
            onClick={() => setOpenModal(true)} 
            className="rounded-xl bg-white px-8 py-4 font-semibold text-indigo-600 hover:bg-gray-100">
              {t("MarketplaceLivreur.primaryCTA")}
            </button>
            <button
            onClick={() => setOpenModal(true)}
             className="rounded-xl border border-white/40 px-8 py-4 font-semibold hover:bg-white/10">
              {t("MarketplaceLivreur.secondaryCTA")}
            </button>
          </div>
        </motion.div>
      </div>

      {/* WHAT IS */}
      <div className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 grid gap-16 lg:grid-cols-2">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-3xl font-bold text-gray-900">
              {t("MarketplaceLivreur.whatIsTitle")}
            </h2>

            <p className="mt-4 text-gray-600">
              {t("MarketplaceLivreur.whatIsDescription")}
            </p>

            <ul className="mt-6 space-y-3 text-gray-700">
              {benefits.map((item, i) => (
                <li key={i}> {item}</li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="space-y-6"
          >
            <h3 className="text-2xl font-semibold text-gray-900">
              {t("MarketplaceLivreur.howItWorksTitle")}
            </h3>

            {steps.map((step, i) => (
              <div
                key={i}
                className="rounded-xl bg-white border p-6 shadow-sm"
              >
                <strong>{i + 1}.</strong> {step}
              </div>
            ))}
          </motion.div>

        </div>
      </div>

      {/* WHY JOIN */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl font-bold text-gray-900"
          >
            {t("MarketplaceLivreur.whyJoinTitle")}
          </motion.h2>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {whyJoinList.map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="rounded-xl bg-gray-50 p-6 text-gray-700"
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* TRUST */}
      <div className="bg-gray-900 py-24 text-white text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mx-auto max-w-4xl px-6"
        >
          <h2 className="text-3xl text-white font-bold">
            {t("MarketplaceLivreur.trustTitle")}
          </h2>

          <p className="mt-4 text-gray-300">
            {t("MarketplaceLivreur.trustDescription")}
          </p>
        </motion.div>
      </div>

      {/* CITIES */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl font-bold text-gray-900"
          >
            {t("MarketplaceLivreur.citiesTitle")}
          </motion.h2>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {cities.map((city, i) => (
              <span
                key={i}
                className="rounded-full bg-indigo-50 px-6 py-2 font-semibold text-indigo-600"
              >
                {city}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* FINAL */}
      <div className="bg-indigo-600 py-24 text-white text-center">
        <h2 className="text-4xl font-bold">
          {t("MarketplaceLivreur.finalTitle")}
        </h2>
      </div>
<LivreurInfoModal
  open={openModal}
  onClose={() => setOpenModal(false)}
/>

    </section>
  );
}
